import { useState } from 'react';
import axios from 'axios';
import AppURL from '../components/AppUrl';
import { useNavigate } from 'react-router-dom';

interface Reservation {
    _id: string;
    fullName: string;
    guests: number;
    date: string;
    time: string;
    preferredLanguage: string;
    contactMethod: string;
}
interface CancelProps {
    language: string;
   

}

const translations = {
    fr: {
        cancelTitle: 'Annuler une réservation',
        emailLabel: 'Email utilisé pour la réservation',
        dateLabel: 'Date de réservation',
        searchBtn: 'Rechercher mes réservations',
        searching: 'Recherche en cours...',
        noReservations: 'Aucune réservation trouvée avec ces informations.',
        errorSearch: 'Erreur lors de la recherche des réservations. Veuillez réessayer.',
        selectTitle: 'Vos réservations pour le',
        back: 'Retour',
        cancelConfirmTitle: 'Annuler votre réservation',
        name: 'Nom',
        date: 'Date',
        time: 'Heure',
        guests: 'Nombre de personnes',
        phoneLabel: 'Entrez votre numéro de téléphone pour confirmer l\'annulation',
        cancelBtn: 'Annuler ma réservation',
        processing: 'Traitement...',
        cancelSuccess: 'Votre réservation a été annulée avec succès.',
        cancelError: 'Erreur lors de l\'annulation de la réservation. Veuillez réessayer.',
        phoneMismatch: 'Numéro de téléphone incorrect. Veuillez utiliser le numéro associé à cette réservation.'

    },
    en: {
        cancelTitle: 'Cancel a Reservation',
        emailLabel: 'Email used for the reservation',
        dateLabel: 'Reservation Date',
        searchBtn: 'Search My Reservations',
        searching: 'Searching...',
        noReservations: 'No reservation found with this information.',
        errorSearch: 'Error while searching reservations. Please try again.',
        selectTitle: 'Your reservations for',
        back: 'Back',
        cancelConfirmTitle: 'Cancel your reservation',
        name: 'Name',
        date: 'Date',
        time: 'Time',
        guests: 'Number of guests',
        phoneLabel: 'Enter your phone number to confirm cancellation',
        cancelBtn: 'Cancel my reservation',
        processing: 'Processing...',
        cancelSuccess: 'Your reservation has been successfully cancelled.',
        cancelError: 'Error while cancelling the reservation. Please try again.',
        phoneMismatch: 'Incorrect phone number. Please use the one associated with the reservation.'


    }
};

const CancelReservation = ({ language }: CancelProps) => {
    const navigate = useNavigate();
    const t = translations[language as keyof typeof translations];
   

    const [step, setStep] = useState<number>(1);
    const [email, setEmail] = useState<string>('');
    const [date, setDate] = useState<string>('');
    const [reservations, setReservations] = useState<Reservation[]>([]);
    const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
    const [phone, setPhone] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleSearchReservations = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const response = await axios.get(`${AppURL}/api/reservations/find`, {
                params: { email, date }
            });

            const result = response.data;
            if (!Array.isArray(result.data) || result.data.length === 0) {
                setError(t.noReservations);
            } else {
                setReservations(result.data);
                setStep(2);
            }
        } catch (err) {
            setError(t.errorSearch);
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelectReservation = (reservation: Reservation) => {
        setSelectedReservation(reservation);
        setStep(3);
    };

    const handleCancelReservation = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedReservation || !phone) return;

        setIsLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await axios.post(`${AppURL}/api/reservations/cancel`, {
                reservationId: selectedReservation._id,
                phone: phone
            });

            if (response.data.success) {
                setSuccess(t.cancelSuccess);
                setTimeout(() => navigate('/'), 3000);
            }
        } catch (err: any) {
            const backendMessage = err.response?.data?.message;

            // Traduire en fonction du message d'erreur reçu
            if (backendMessage === 'Numéro de téléphone incorrect. Veuillez utiliser le numéro associé à cette réservation.') {
                setError(t.phoneMismatch || backendMessage);
            } else {
                setError(backendMessage || t.cancelError);
            }

            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <div className="min-h-screen flex flex-col items-center p-8 bg-cover bg-center bg-fixed pt-[110px]"
            style={{ backgroundImage: "url('/image/Lona2.jpg')" }}>

           



            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-8 max-w-2xl w-full shadow-lg">
                <h2 className="text-2xl font-bold text-green-950 mb-6 text-center">{t.cancelTitle}</h2>

                {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}
                {success && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">{success}</div>}

                {step === 1 && (
                    <form onSubmit={handleSearchReservations} className="space-y-6">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">{t.emailLabel}</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-950 focus:border-green-950"
                            />
                        </div>
                        <div>
                            <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">{t.dateLabel}</label>
                            <input
                                type="date"
                                id="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                required
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-950 focus:border-green-950"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className={`w-full py-3 px-4 text-white font-medium rounded-lg transition duration-200 ${isLoading ? 'bg-gray-400' : 'bg-green-950 hover:bg-green-800'}`}>
                            {isLoading ? t.searching : t.searchBtn}
                        </button>
                    </form>
                )}

                {step === 2 && (
                    <div>
                        <h3 className="text-lg font-semibold mb-4">{t.selectTitle} {date}</h3>
                        {reservations.length === 0 ? (
                            <p>{t.noReservations}</p>
                        ) : (
                            <div className="space-y-4">
                                {reservations.map((reservation) => (
                                    <div key={reservation._id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer" onClick={() => handleSelectReservation(reservation)}>
                                        <div className="flex justify-between">
                                            <span className="font-medium">{reservation.fullName}</span>
                                            <span>{reservation.time}</span>
                                        </div>
                                        <div className="text-sm text-gray-600">{reservation.guests} {t.guests}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <button onClick={() => setStep(1)} className="mt-6 w-full py-2 px-4 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300">{t.back}</button>
                    </div>
                )}

                {step === 3 && selectedReservation && (
                    <div>
                        <h3 className="text-lg font-semibold mb-4">{t.cancelConfirmTitle}</h3>
                        <div className="border border-gray-200 rounded-lg p-4 mb-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div><p className="text-sm text-gray-500">{t.name}</p><p className="font-medium">{selectedReservation.fullName}</p></div>
                                <div><p className="text-sm text-gray-500">{t.date}</p><p className="font-medium">{selectedReservation.date}</p></div>
                                <div><p className="text-sm text-gray-500">{t.time}</p><p className="font-medium">{selectedReservation.time}</p></div>
                                <div><p className="text-sm text-gray-500">{t.guests}</p><p className="font-medium">{selectedReservation.guests}</p></div>
                            </div>
                        </div>

                        <form onSubmit={handleCancelReservation} className="space-y-6">
                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">{t.phoneLabel}</label>
                                <input
                                    type="tel"
                                    id="phone"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    required
                                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-950 focus:border-green-950"
                                />
                            </div>
                            <div className="flex space-x-4">
                                <button type="button" onClick={() => setStep(2)} className="flex-1 py-2 px-4 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300">{t.back}</button>
                                <button type="submit" disabled={isLoading} className={`flex-1 py-2 px-4 text-white font-medium rounded-lg transition duration-200 ${isLoading ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'}`}>
                                    {isLoading ? t.processing : t.cancelBtn}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CancelReservation;
